/**
 * Simulates a participant in the video call for testing purposes.
 * Creates fake video/audio streams and connects to the room.
 */

import { FakeVideoGenerator, TestPattern } from './FakeVideoGenerator';
import { BandwidthSimulator, NetworkProfile, NETWORK_PROFILES, ProfileName } from './BandwidthSimulator';
import { io, Socket } from 'socket.io-client';
import type { ServerToClientEvents, ClientToServerEvents, FrameChunk, FrameMetadata } from '@modelcast/types';

export interface SimulatedParticipantOptions {
  name: string;
  role: 'model' | 'artist';
  videoPattern?: TestPattern;
  resolution?: '360p' | '720p' | '1080p' | '4k';
  networkProfile?: ProfileName | NetworkProfile;
  autoJoin?: boolean;
}

export interface ParticipantStats {
  framesSent: number;
  framesReceived: number;
  bytesSent: number;
  bytesReceived: number;
  averageFrameTime: number;
  droppedFrames: number;
}

export class SimulatedParticipant {
  private options: Required<SimulatedParticipantOptions>;
  private videoGenerator: FakeVideoGenerator;
  private bandwidthSimulator: BandwidthSimulator;
  private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
  private participantId: string | null = null;
  private roomCode: string | null = null;
  private isConnected = false;
  private isStreaming = false;
  private animationFrameId: number | null = null;
  private stats: ParticipantStats = {
    framesSent: 0,
    framesReceived: 0,
    bytesSent: 0,
    bytesReceived: 0,
    averageFrameTime: 0,
    droppedFrames: 0,
  };
  private frameTimes: number[] = [];
  private onFrameReceived?: (metadata: FrameMetadata, assembledImage: Blob) => void;

  constructor(options: SimulatedParticipantOptions) {
    const resolutions = {
      '360p': { width: 640, height: 360 },
      '720p': { width: 1280, height: 720 },
      '1080p': { width: 1920, height: 1080 },
      '4k': { width: 3840, height: 2160 },
    };

    const resolution = options.resolution || (options.role === 'model' ? '4k' : '360p');
    const { width, height } = resolutions[resolution];

    this.options = {
      name: options.name,
      role: options.role,
      videoPattern: options.videoPattern || (options.role === 'model' ? 'grid' : 'color-bars'),
      resolution,
      networkProfile: options.networkProfile || 'cable',
      autoJoin: options.autoJoin ?? true,
    };

    this.videoGenerator = new FakeVideoGenerator({
      width,
      height,
      frameRate: 30, // Maximum frame rate for all participants
      pattern: this.options.videoPattern,
      label: `${options.name} (${options.role})`,
    });

    this.bandwidthSimulator = new BandwidthSimulator(this.options.networkProfile);
  }

  async joinRoom(roomCode: string, apiUrl: string = 'http://localhost:3001'): Promise<void> {
    this.roomCode = roomCode;

    // Join room via API
    const response = await fetch(`${apiUrl}/api/rooms/${roomCode}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        displayName: this.options.name,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to join room: ${response.statusText}`);
    }

    const data = await response.json();
    this.participantId = data.participant.id;

    // Connect to Socket.IO
    this.socket = io(apiUrl, {
      transports: ['websocket'],
    });

    // Wrap socket with bandwidth simulator
    // (Note: full implementation would intercept all messages)

    await new Promise<void>((resolve, reject) => {
      if (!this.socket) return reject(new Error('Socket not initialized'));

      this.socket.on('connect', () => {
        const joinData = {
          participantId: this.participantId!,
          displayName: this.options.name,
          role: this.options.role,
          isHost: false,
        };
        this.socket!.emit('room:join', roomCode, joinData, (response) => {
          if (response.success) {
            this.isConnected = true;
            console.log(`[${this.options.name}] Joined room ${roomCode}`);
            resolve();
          } else {
            reject(new Error(response.error || 'Failed to join room'));
          }
        });
      });

      this.socket.on('connect_error', (err) => {
        reject(err);
      });

      // Set up frame receiving for artists
      if (this.options.role === 'artist') {
        this.setupFrameReceiving();
      }
    });
  }

  private setupFrameReceiving(): void {
    if (!this.socket) return;

    const currentFrameChunks = new Map<number, ArrayBuffer>();
    let currentMetadata: FrameMetadata | null = null;
    let frameStartTime = 0;

    this.socket.on('frame:metadata', async (metadata) => {
      currentMetadata = metadata;
      currentFrameChunks.clear();
      frameStartTime = Date.now();
      console.log(`[${this.options.name}] Receiving frame ${metadata.frameId} (${metadata.totalChunks} chunks)`);
    });

    this.socket.on('frame:chunk', async (chunk) => {
      // Simulate network delay for receiving
      try {
        await this.bandwidthSimulator.receive(chunk.data);
        currentFrameChunks.set(chunk.chunkIndex, chunk.data);
        this.stats.bytesReceived += chunk.data.byteLength;

        // Check if frame is complete
        if (currentMetadata && currentFrameChunks.size === currentMetadata.totalChunks) {
          const frameTime = Date.now() - frameStartTime;
          this.frameTimes.push(frameTime);
          this.stats.framesReceived++;
          this.stats.averageFrameTime = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;

          console.log(`[${this.options.name}] Frame complete in ${frameTime}ms`);

          // Assemble and notify
          if (this.onFrameReceived && currentMetadata) {
            const assembled = this.assembleFrame(currentFrameChunks, currentMetadata);
            this.onFrameReceived(currentMetadata, assembled);
          }
        }
      } catch (err) {
        this.stats.droppedFrames++;
        console.log(`[${this.options.name}] Dropped chunk (simulated packet loss)`);
      }
    });
  }

  private assembleFrame(chunks: Map<number, ArrayBuffer>, metadata: FrameMetadata): Blob {
    const sortedChunks = Array.from(chunks.entries())
      .sort(([a], [b]) => a - b)
      .map(([_, data]) => data);

    return new Blob(sortedChunks, { type: `image/${metadata.format}` });
  }

  async startStreaming(): Promise<void> {
    if (this.options.role !== 'model') {
      console.warn('Only models can stream high-res frames');
      return;
    }

    // Start the video generator
    this.videoGenerator.start();
    this.isStreaming = true;

    // Continuous streaming - send frames as fast as possible
    // Each frame is sent only after the previous one completes
    const streamLoop = async () => {
      if (!this.isStreaming) return;

      await this.captureAndSendFrame();

      // Use requestAnimationFrame for smooth continuous streaming
      // This naturally limits to ~60fps but actual send rate depends on bandwidth
      if (this.isStreaming) {
        this.animationFrameId = requestAnimationFrame(() => {
          streamLoop();
        });
      }
    };

    // Start the continuous stream
    streamLoop();
  }

  private async captureAndSendFrame(): Promise<void> {
    if (!this.socket || !this.isConnected) return;

    const canvas = this.videoGenerator.getCanvas();
    const startTime = Date.now();

    try {
      // Capture frame as blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Failed to capture'))),
          'image/webp',
          0.95
        );
      });

      const arrayBuffer = await blob.arrayBuffer();
      const chunkSize = 64000;
      const totalChunks = Math.ceil(arrayBuffer.byteLength / chunkSize);
      const frameId = `sim-${Date.now()}-${this.stats.framesSent}`;

      // Send metadata
      const metadata: FrameMetadata = {
        frameId,
        width: canvas.width,
        height: canvas.height,
        format: 'webp',
        totalSize: arrayBuffer.byteLength,
        totalChunks,
        timestamp: Date.now(),
        modelId: this.participantId || '',
      };

      this.socket.emit('frame:start', metadata);

      // Send chunks with simulated bandwidth
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, arrayBuffer.byteLength);
        const chunkData = arrayBuffer.slice(start, end);

        try {
          // Simulate upload bandwidth
          await this.bandwidthSimulator.send(chunkData);

          const chunk: FrameChunk = {
            frameId,
            chunkIndex: i,
            totalChunks,
            data: chunkData,
            timestamp: Date.now(),
          };

          this.socket.emit('frame:chunk', chunk);
          this.stats.bytesSent += chunkData.byteLength;
        } catch (err) {
          this.stats.droppedFrames++;
          console.log(`[${this.options.name}] Chunk ${i} dropped (simulated)`);
        }
      }

      this.stats.framesSent++;
      const frameTime = Date.now() - startTime;
      this.frameTimes.push(frameTime);
      this.stats.averageFrameTime = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;

      console.log(`[${this.options.name}] Frame sent in ${frameTime}ms (${(arrayBuffer.byteLength / 1024 / 1024).toFixed(2)} MB)`);
    } catch (err) {
      console.error(`[${this.options.name}] Error sending frame:`, err);
    }
  }

  stopStreaming(): void {
    this.isStreaming = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.videoGenerator.stop();
  }

  disconnect(): void {
    this.stopStreaming();
    if (this.socket) {
      this.socket.emit('room:leave');
      this.socket.close();
      this.socket = null;
    }
    this.isConnected = false;
  }

  setNetworkProfile(profile: ProfileName | NetworkProfile): void {
    this.bandwidthSimulator.setProfile(profile);
  }

  setVideoPattern(pattern: TestPattern): void {
    this.videoGenerator.setPattern(pattern);
  }

  onFrame(callback: (metadata: FrameMetadata, image: Blob) => void): void {
    this.onFrameReceived = callback;
  }

  getStats(): ParticipantStats {
    return { ...this.stats };
  }

  getName(): string {
    return this.options.name;
  }

  getRole(): 'model' | 'artist' {
    return this.options.role;
  }

  isModel(): boolean {
    return this.options.role === 'model';
  }

  getVideoStream(): MediaStream {
    return this.videoGenerator.start();
  }
}

/**
 * Test harness for running multi-participant simulations
 */
export class TestHarness {
  private participants: SimulatedParticipant[] = [];
  private roomCode: string | null = null;

  async createRoom(apiUrl: string = 'http://localhost:3001', roomName: string = 'Test Session'): Promise<string> {
    const response = await fetch(`${apiUrl}/api/rooms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: roomName }),
    });

    if (!response.ok) {
      throw new Error('Failed to create room');
    }

    const data = await response.json();
    this.roomCode = data.room.code;
    return this.roomCode!;
  }

  async addParticipant(options: SimulatedParticipantOptions): Promise<SimulatedParticipant> {
    if (!this.roomCode) {
      throw new Error('Create a room first');
    }

    const participant = new SimulatedParticipant(options);
    await participant.joinRoom(this.roomCode);
    this.participants.push(participant);

    return participant;
  }

  async addMultipleArtists(count: number, networkProfile?: ProfileName): Promise<SimulatedParticipant[]> {
    const artists: SimulatedParticipant[] = [];

    for (let i = 0; i < count; i++) {
      const artist = await this.addParticipant({
        name: `Artist ${i + 1}`,
        role: 'artist',
        resolution: '360p',
        networkProfile: networkProfile || 'cable',
      });
      artists.push(artist);
    }

    return artists;
  }

  getParticipants(): SimulatedParticipant[] {
    return [...this.participants];
  }

  getModel(): SimulatedParticipant | undefined {
    return this.participants.find(p => p.isModel());
  }

  getAllStats(): Record<string, ParticipantStats> {
    const stats: Record<string, ParticipantStats> = {};
    for (const p of this.participants) {
      stats[p.getName()] = p.getStats();
    }
    return stats;
  }

  disconnectAll(): void {
    for (const p of this.participants) {
      p.disconnect();
    }
    this.participants = [];
  }
}
